import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";
import type { z } from "zod";

import {
  DecideAssessmentRequestSchema,
  type DecideAssessmentRequest,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

/**
 * How a decision went: recorded; refused because the assessment moved on
 * while it was being made; or not recorded for any other reason.
 *
 * `changed` is told apart because the approver can do something about it —
 * read the assessment as it now stands — where a failure only asks them to try
 * again.
 */
export type DecisionOutcome = "decided" | "changed" | "failed";

export type ReturnRequest = Extract<
  DecideAssessmentRequest,
  { outcome: "returned" }
>;

/**
 * The message key a failed parse is refused with, or `undefined` where it
 * succeeded.
 */
const reasonError = (
  parsed: z.ZodSafeParseResult<unknown>,
): string | undefined =>
  parsed.success ? undefined : parsed.error.issues[0]?.message;

/** What a decision that was not recorded says, keyed by why. */
const NOT_RECORDED = {
  changed: "assessment.decision.changed",
  failed: "assessment.decision.failed",
} as const;

/**
 * The Approver's decision on a submitted assessment: approve it, or return it
 * with a reason. Both are decisions on the whole assessment, and both are
 * confirmed first, because neither can be undone by the approver.
 *
 * Shown only to somebody who may decide; whether they may is the caller's to
 * say.
 */
export const DecisionPanel = ({
  assessorName,
  personName,
  onApprove,
  onReturn,
}: {
  assessorName: string;
  personName: string;
  onApprove: () => Promisable<DecisionOutcome>;
  onReturn: (request: ReturnRequest) => Promisable<DecisionOutcome>;
}) => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [returning, setReturning] = useState(false);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6" component="h2">
          {t("assessment.decision.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("assessment.decision.explanation", { assessor: assessorName })}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setApproving(true);
          }}
        >
          {t("assessment.decision.approve")}
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setReturning(true);
          }}
        >
          {t("assessment.decision.return")}
        </Button>
      </Stack>

      <ApproveDialog
        open={approving}
        personName={personName}
        onClose={() => {
          setApproving(false);
        }}
        onApprove={onApprove}
      />
      <ReturnDialog
        open={returning}
        assessorName={assessorName}
        onClose={() => {
          setReturning(false);
        }}
        onReturn={onReturn}
      />
    </Paper>
  );
};

/**
 * Sends a decision and keeps what came of it, so a dialog stays open on a
 * decision that was not recorded and says why.
 */
const useSending = () => {
  const [sending, setSending] = useState(false);
  const [notRecorded, setNotRecorded] = useState<
    Exclude<DecisionOutcome, "decided"> | undefined
  >();

  const send = async (decide: () => Promisable<DecisionOutcome>) => {
    setSending(true);
    setNotRecorded(undefined);
    let outcome: DecisionOutcome;

    try {
      outcome = await decide();
    } catch {
      outcome = "failed";
    }

    setSending(false);

    if (outcome !== "decided") {
      setNotRecorded(outcome);
    }

    return outcome;
  };

  return {
    sending,
    notRecorded,
    send,
    reset: () => {
      setNotRecorded(undefined);
    },
  };
};

/** Confirms an approval, which cannot be undone. */
const ApproveDialog = ({
  open,
  personName,
  onClose,
  onApprove,
}: {
  open: boolean;
  personName: string;
  onClose: () => Promisable<void>;
  onApprove: () => Promisable<DecisionOutcome>;
}) => {
  const { t } = useTranslation();
  const { sending, notRecorded, send, reset } = useSending();

  const close = () => {
    reset();
    void onClose();
  };

  return (
    <Dialog
      open={open}
      // Nothing closes the dialog while a decision is on its way, so its
      // outcome is never lost.
      onClose={sending ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("assessment.decision.approveDialog.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {t("assessment.decision.approveDialog.body", {
              personName: personName,
            })}
          </DialogContentText>
          {notRecorded === undefined ? null : (
            <Alert severity="error">{t(NOT_RECORDED[notRecorded])}</Alert>
          )}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: sending,
        }}
        proceed={{
          label: t("assessment.decision.approveDialog.confirm"),
          onClick: () => {
            void send(onApprove).then((outcome) => {
              if (outcome === "decided") {
                close();
              }
            });
          },
          disabled: sending,
          kind: "primary",
        }}
      />
    </Dialog>
  );
};

/**
 * Returns the whole assessment to its assessor with a reason. The reason is
 * the only way to point at what needs another look, so there is no returning
 * without one.
 */
const ReturnDialog = ({
  open,
  assessorName,
  onClose,
  onReturn,
}: {
  open: boolean;
  assessorName: string;
  onClose: () => Promisable<void>;
  onReturn: (request: ReturnRequest) => Promisable<DecisionOutcome>;
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const { sending, notRecorded, send, reset } = useSending();

  // The server's own schema, so the dialog cannot send a reason it refuses.
  const parsed = DecideAssessmentRequestSchema.safeParse({
    outcome: "returned",
    reason,
  });
  const error = reasonError(parsed);
  // Spaces alone are no reason, so there is nothing to press until one is
  // written.
  const written = reason.trim() !== "";

  const close = () => {
    setReason("");
    setReasonTouched(false);
    reset();
    void onClose();
  };

  const submit = () => {
    setReasonTouched(true);

    if (!parsed.success || parsed.data.outcome !== "returned") {
      return;
    }

    const request = parsed.data;
    void send(() => onReturn(request)).then((outcome) => {
      if (outcome === "decided") {
        close();
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("assessment.decision.returnDialog.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {t("assessment.decision.returnDialog.intro", {
              assessor: assessorName,
            })}
          </DialogContentText>
          <DialogContentText variant="body2">
            {t("assessment.decision.returnDialog.explanation", {
              assessor: assessorName,
            })}
          </DialogContentText>
          <TextField
            id="return-assessment-reason"
            label={t("assessment.decision.returnDialog.reason")}
            placeholder={t(
              "assessment.decision.returnDialog.reasonPlaceholder",
            )}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            onBlur={() => {
              setReasonTouched(true);
            }}
            error={reasonTouched && written && error !== undefined}
            helperText={
              reasonTouched && written && error !== undefined
                ? t(error)
                : t("assessment.decision.returnDialog.reasonHelp", {
                    assessor: assessorName,
                  })
            }
            disabled={sending}
            multiline
            minRows={4}
            fullWidth
            required
          />
          {notRecorded === undefined ? null : (
            <Alert severity="error">{t(NOT_RECORDED[notRecorded])}</Alert>
          )}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: sending,
        }}
        proceed={{
          label: t("assessment.decision.returnDialog.confirm"),
          onClick: submit,
          disabled: sending || !written,
          kind: "primary",
        }}
      />
    </Dialog>
  );
};
